import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store'
import { reloadForChunkError } from '@/utils/chunk-reload'
import { sanitizeRedirect, isNonSpaPath } from '@/utils/post-login-redirect'
import { lockedRedirectFor } from '@/utils/org-lifecycle'

// Import views
import Home from '@/views/Home.vue'
import Technical from '@/views/Technical.vue'
import Pricing from '@/views/Pricing.vue'
import Contact from '@/views/Contact.vue'
import NotFound from '@/views/NotFound.vue'
import ResetPassword from '@/views/ResetPassword.vue'
import Activate from '@/views/Activate.vue'
import Docs from '@/views/Docs.vue'
import { resolveDoorRoute } from '@/utils/persona'
import { seoMeta, resolvePageMeta, applyPageMeta } from '@/utils/page-meta'
import { getEnvConfig } from '@/utils/env-config'
import en from '@/locales/english.json'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: {
      ...seoMeta(en, 'home')
    }
  },
  {
    // Persona doors (documents/WEBSITE_REBUILD_PERSONA_DOORS_AND_CONTENT_MATRIX.md
    // §4, §7). Five real URLs so they can be linked and indexed; one
    // content-driven template (src/content/doors.js). An unknown id is a 404,
    // not an empty page.
    path: '/for/:door',
    name: 'DoorLanding',
    component: () => import('@/views/DoorLanding.vue'),
    beforeEnter(to) {
      return resolveDoorRoute(to.params.door) ? true : { path: '/404', replace: true }
    },
    meta: {
      // Title/description resolve per door from seo.pages.<door> (src/utils/page-meta.js).
      seo: 'door',
      public: true,
      title: en.seo.default.title,
      description: en.seo.default.description
    }
  },
  {
    // Sign-up entry point used by the door CTAs ("Start a trial"). Mirrors
    // /signin: renders Home with the auth modal open in register mode and
    // lands the new account on the customer dashboard.
    path: '/signup',
    name: 'SignUp',
    component: Home,
    beforeEnter() {
      if (store.state.auth.isAuthenticated) return '/admin'
      store.commit('setAuthModalType', 'register')
      store.commit('setShowAuthModal', true)
      store.commit('setPostAuthRedirect', '/admin')
      return true
    },
    meta: {
      title: 'Sign up - DenvStack',
      description: 'Create your DenvStack account'
    }
  },
  {
    path: '/technical',
    name: 'Technical',
    component: Technical,
    meta: {
      title: 'Technical Architecture - DenvStack',
      description: 'Technical infrastructure and architecture details',
      requiresAuth: true
    }
    // Route guard handled globally in main.js
  },
  {
    // /platform — public architecture page for the engineering-lead reader
    // (persona doors phase 2). /technical stays auth-gated and unchanged.
    path: '/platform',
    name: 'Platform',
    component: () => import('@/views/Platform.vue'),
    meta: {
      ...seoMeta(en, 'platform')
    }
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: Pricing,
    meta: {
      ...seoMeta(en, 'pricing')
    }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: Contact,
    meta: {
      ...seoMeta(en, 'contact')
    }
  },
  {
    path: '/docs',
    name: 'Docs',
    // The docs portal is now served by platform-dashboard at /docs via the
    // ingress, NOT by this SPA. The in-SPA Docs.vue engine (which called the
    // retired /api/docs backend) is left in place but must never render — any
    // client-side navigation to /docs forces a real browser load so the
    // request reaches the ingress → platform-dashboard. No loop: a full load
    // of /docs is served by platform-dashboard, not the SPA.
    component: Docs,
    beforeEnter() {
      // Abort the in-SPA navigation and hand off to a full browser load.
      window.location.href = '/docs'
    },
    meta: {
      title: 'Documentation - DenvStack',
      description: 'Comprehensive documentation for DenvStack platform'
    }
  },
  {
    // Sign-in entry point for same-origin platform pages (platform-dashboard's
    // /status, /docs, /tools, ...): `/signin?redirect=<path>`. Sign-in itself
    // is the auth MODAL, so this route renders Home with the modal open and the
    // (sanitised) return path stashed as postAuthRedirect. An already
    // signed-in visitor is sent straight back — without this the dashboard's
    // "sign in" link looped onto the customer dashboard.
    path: '/signin',
    alias: '/login',
    name: 'SignIn',
    component: Home,
    beforeEnter(to) {
      const target = sanitizeRedirect(to.query.redirect)
      if (store.state.auth.isAuthenticated) {
        if (target && isNonSpaPath(target)) {
          // Cookie was (re)asserted at app start / on login — full load.
          window.location.assign(target)
          return false
        }
        return target || '/admin'
      }
      store.commit('setAuthModalType', 'login')
      store.commit('setShowAuthModal', true)
      // No (valid) return path → land on the customer dashboard after login.
      store.commit('setPostAuthRedirect', target || '/admin')
      return true
    },
    meta: {
      title: 'Sign in - DenvStack',
      description: 'Sign in to DenvStack'
    }
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@/views/Dashboard.vue'),
    meta: {
      title: 'Dashboard - DenvStack',
      description: 'Admin dashboard for managing your development environments',
      requiresAuth: true
    }
  },
  {
    // Operator surface — Environments console. A services × environments
    // matrix (development / stage / main) over the denv-new config repo, plus
    // a Create-Stage wizard that promotes a service's development config into
    // a new stage environment. Operator / super-admin only: the view itself
    // gates on is_super_admin / is_admin (renders an access state otherwise),
    // and authModalOnDeny opens the auth modal in place on a logged-out direct
    // nav — matching the rest of the auth-gated routes.
    path: '/admin/environments',
    name: 'Environments',
    component: () => import('@/views/Environments.vue'),
    meta: {
      title: 'Environments - DenvStack',
      description: 'Create and manage stage environments for platform services',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Standard #18 E2 — one platform face in the operator console: Overview
    // (row vs target with drift), Services, Databases, Secrets (public halves
    // only), Deploy, History, plus the Configure wizard (plan → diff → apply).
    // Members land on a reduced page from the redacted read (I8), never a 403.
    // Declared before `/admin/environments/:id` so the static `platform`
    // segment is matched first.
    path: '/admin/environments/platform/:slug',
    name: 'PlatformEnvironmentDetail',
    component: () => import('@/views/EnvironmentDetail.vue'),
    meta: {
      title: 'Platform environment - DenvStack',
      description: 'One platform environment: services, databases, secrets, deploy targets and configuration',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Platform Environment Registry — P1 (DARK). Env workspace detail: a
    // tabbed read-only view (Domains · Certificates · Volumes · Backups ·
    // Members) of one customer environment, reached via "Manage" from the
    // org-mode env cards on /admin/environments. The cluster-api endpoints are
    // dark behind ENVIRONMENT_PROVISIONING_ENABLED; the website surface is
    // reachable only when VUE_APP_ENVIRONMENT_REGISTRY_ENABLED is on (no menu
    // entry points here otherwise). Same auth guard as the parent route.
    path: '/admin/environments/:id',
    name: 'EnvWorkspaceDetail',
    component: () => import('@/views/EnvWorkspaceDetail.vue'),
    meta: {
      title: 'Environment - DenvStack',
      description: 'Domains, certificates, volumes, backups and members for one environment',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Customer environments (owner decision 2026-10-22): "apps live in
    // environments, every organisation has one for free, more cost money".
    // Every signed-in customer sees their organisation's environments with
    // the default first; creating more is tier-gated by `max_environments`
    // (cluster-api 2.71.0). Distinct from the operator console
    // (/admin/environments) and the platform faces (/environments).
    path: '/my/environments',
    name: 'MyEnvironments',
    component: () => import('@/views/MyEnvironments.vue'),
    meta: {
      title: 'Environments - DenvStack',
      description: 'Where your apps run: your organisation’s environments, the one included in your plan and any you add',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // One customer environment — the same tabbed workspace detail the
    // operator console opens from its org cards, reached from
    // /my/environments so the breadcrumb returns there.
    path: '/my/environments/:id',
    name: 'MyEnvironmentDetail',
    component: () => import('@/views/EnvWorkspaceDetail.vue'),
    meta: {
      title: 'Environment - DenvStack',
      description: 'Domains, certificates, volumes, backups and members for one environment',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Standard #18 I8 — the member-visible platform environments page: the
    // seven faces as cards from the redacted list, for any signed-in user.
    // Operators get an "Open in console" link per face.
    path: '/environments',
    name: 'PlatformEnvironments',
    component: () => import('@/views/PlatformEnvironments.vue'),
    meta: {
      title: 'Platform environments - DenvStack',
      description: 'The platform faces: where each runs, its hostname and whether it matches the standard',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Phase G v1.2 — organization member management. Owners/admins invite
    // teammates here and review pending invitations.
    path: '/admin/members',
    name: 'Members',
    component: () => import('@/views/Members.vue'),
    meta: {
      title: 'Members - DenvStack',
      description: 'Manage organization members and invitations',
      requiresAuth: true
    }
  },
  {
    // Phase G v1.2 — invitation email-link landing. Authenticated users get
    // routed into /admin/members; logged-out visitors are bounced to home
    // with the token preserved for the registration flow to consume.
    path: '/invitations/:token/accept',
    name: 'InvitationAccept',
    component: () => import('@/views/InvitationAccept.vue'),
    meta: {
      title: 'Accepting invitation - DenvStack'
    }
  },
  {
    // Marketplace Phase 1 — public, anonymous browse experience. Lazy-loaded
    // and NEVER auth-gated (the catalog endpoints are anonymous).
    path: '/marketplace',
    name: 'Marketplace',
    component: () => import('@/views/Marketplace.vue'),
    meta: {
      ...seoMeta(en, 'marketplace')
    }
  },
  {
    // Marketplace Phase 1 — public app detail / documentation page.
    path: '/marketplace/:id',
    name: 'MarketplaceDetail',
    component: () => import('@/views/MarketplaceDetail.vue'),
    meta: {
      title: 'App Details - DenvStack',
      description: 'App documentation, components and versions'
    }
  },
  {
    // Marketplace Phase 2 — native deploy wizard (auth-gated). When reached
    // logged-out, the guard opens the auth modal (authModalOnDeny) and stashes
    // postAuthRedirect so login continues straight back here.
    path: '/marketplace/:id/deploy',
    name: 'DeployApp',
    component: () => import('@/views/DeployApp.vue'),
    meta: {
      title: 'Deploy App - DenvStack',
      description: 'Deploy a marketplace app into your workspace',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Marketplace Phase 3 — the authenticated "My Apps" dashboard (table of
    // the user's deployed UTS instances). authModalOnDeny opens the auth
    // modal in place and stashes postAuthRedirect (design spec §1.2).
    path: '/my/apps',
    name: 'MyApps',
    component: () => import('@/views/MyApps.vue'),
    meta: {
      title: 'My Apps - DenvStack',
      description: 'Manage the apps you have deployed from the marketplace',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Execution plane R1b — the customer projection of one runbook run (visible
    // steps only). Declared before `/my/apps/:id` so `runs` is never read as
    // an instance id.
    path: '/my/apps/runs/:runId',
    name: 'MyRunDetail',
    component: () => import('@/views/MyRunDetail.vue'),
    meta: {
      title: 'Blueprint run - DenvStack',
      description: 'Progress and steps of a blueprint run',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Marketplace Phase 3 — runtime detail + lifecycle for one instance.
    path: '/my/apps/:id',
    name: 'MyAppDetail',
    component: () => import('@/views/MyAppDetail.vue'),
    meta: {
      title: 'App - DenvStack',
      description: 'Runtime status, pods, snapshots and lifecycle actions',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Marketplace Phase 4 — edit wizard. Reuses DeployWizard in
    // mode="edit" with OCC-guarded PUT + redeploy. authModalOnDeny opens
    // the auth modal in place and stashes postAuthRedirect on direct nav.
    path: '/my/apps/:id/edit',
    name: 'MyAppEdit',
    component: () => import('@/views/EditApp.vue'),
    meta: {
      title: 'Edit App - DenvStack',
      description: 'Edit configuration for a deployed app',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Retired (Workspace Phase 4, design F13): the standalone Clusters page
    // duplicated the Workspace › Clusters tab — same endpoint, copied
    // styling. Old links land on the tab; nothing 404s.
    path: '/my/clusters',
    redirect: { path: '/my/assets', query: { tab: 'clusters' } }
  },
  {
    // Org Asset Management — SOUTH plane. An org admin sees their GRANTED
    // assets (datacenters, clusters, workloads, capacity, GitLab servers).
    // NOT licensed-only — a platform_owner org
    // (Caucasus Systems Basic) sees its owned assets here too. The
    // /my/org-assets endpoint permission-filters to granted assets server-side.
    path: '/my/assets',
    name: 'MyAssets',
    component: () => import('@/views/MyAssets.vue'),
    meta: {
      title: 'Workspace - DenvStack',
      description: 'Your organization workspace — assets, clusters, files, integrations and governance',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Team Collaboration — SOUTH plane. Read-only "Integrations" view: an org
    // member sees their org's collaboration connections (Slack / Jira / AD)
    // and, per destination, the scope_path clearances cleared to receive data.
    // NO secrets are ever shown. authModalOnDeny matches the rest of /my/*.
    path: '/my/integrations',
    name: 'MyIntegrations',
    component: () => import('@/views/MyIntegrations.vue'),
    meta: {
      title: 'Integrations - DenvStack',
      description: 'View your organization\'s collaboration connections and what each destination is cleared to receive',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Deploy Governance — SOUTH plane org-admin view. Manage the org's 4-eyes
    // production-deploy approval policy: the per-org default + per-cs-application
    // overrides. Server-side org-admin-gated (the view shows an "admin required"
    // state on 403). authModalOnDeny matches the rest of /my/*.
    path: '/my/deploy-governance',
    name: 'MyDeployGovernance',
    component: () => import('@/views/MyDeployGovernance.vue'),
    meta: {
      title: 'Deploy Governance - DenvStack',
      description: 'Manage your organization\'s production-deploy approval policy (per-org default + per-application overrides)',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Storage & Files (feature catalog §E) — the authenticated "My Files"
    // manager. Browses the user's own per-namespace volumes (PVCs) over the
    // existing cluster-api PV file-browser. authModalOnDeny opens the auth
    // modal in place and stashes postAuthRedirect, matching the marketplace
    // auth-gated routes.
    path: '/my/files',
    name: 'MyFiles',
    component: () => import('@/views/MyFiles.vue'),
    meta: {
      title: 'My Files - DenvStack',
      description: 'Browse, upload and manage files on your storage volumes',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Phase 5a — customer billing center. Subscription summary, invoices
    // list with PDF download / email re-send, payment-methods (P5a renders
    // the "payments coming soon" informational state until a provider is
    // wired), and notification preferences. authModalOnDeny opens the auth
    // modal in place and stashes postAuthRedirect, matching the rest of
    // the /my/* routes.
    path: '/my/billing',
    name: 'MyBilling',
    component: () => import('@/views/MyBilling.vue'),
    meta: {
      title: 'Billing - DenvStack',
      description: 'Manage your subscription, invoices and billing preferences',
      requiresAuth: true,
      authModalOnDeny: true,
      // Gated on the current org's view-billing grant. The beforeEach guard
      // redirects to /my/apps when the active org definitively reports
      // can_view_billing === false, so a direct URL doesn't expose billing to
      // a technical org-admin. (The view also renders an access-denied state.)
      requiresBilling: true
    }
  },
  {
    // Backups & DR (customer self-service) — schedules, history, targets and
    // restores against the org-scoped backup-api. authModalOnDeny opens the
    // auth modal in place and stashes postAuthRedirect, matching the rest of
    // the /my/* routes.
    path: '/my/backups',
    name: 'MyBackups',
    component: () => import('@/views/MyBackups.vue'),
    meta: {
      title: 'Backups - DenvStack',
      description: 'Schedule backups of your databases and files, manage targets, and restore from history',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Domains & SSL (Unified Certificate Service design v1.5.1 §15.3, Phase
    // 6.5) — the customer page on the Domain object: claim a domain you own
    // with its public provider and datacenter, verify via TXT challenge,
    // manage records by lane (public per provider, private = the platform's
    // internal DNS), and issue certificates under it (the 3.5f Issue view).
    // Deep links: `?tab=domains|records|ssl&domain=example.com`.
    // authModalOnDeny opens the auth modal in place and stashes
    // postAuthRedirect, matching the rest of the /my/* routes.
    path: '/my/domains',
    name: 'MyDomains',
    component: () => import('@/views/MyDomains.vue'),
    meta: {
      title: 'Domains & SSL - DenvStack',
      description: 'Add domains you own, choose who manages their DNS and where they live, verify ownership, manage records on both lanes and issue certificates under them',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Retired (P-25): the dark /my/certificates page folded into the SSL tab
    // of /my/domains. Kept as a redirect so old links land on the Issue view.
    path: '/my/certificates',
    name: 'MyCertificates',
    redirect: { path: '/my/domains', query: { tab: 'ssl' } }
  },
  {
    // P8 — Dev Environments: develop a deployed app in place (browser VS Code
    // + shell) over the cluster-api P6 API. The nav entry is capability-gated
    // (store devEnvironments/probe); the route itself is plain auth-gated and
    // the view renders the disabled/forbidden state inline when the probe
    // says so. Stage 1 ships placeholder views; stage 2 lands the real ones.
    path: '/my/dev-environments',
    name: 'MyDevEnvironments',
    component: () => import('@/views/MyDevEnvironments.vue'),
    meta: {
      title: 'Dev Environments - DenvStack',
      description: 'Develop your deployed apps in place with browser VS Code and a shell',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    path: '/my/dev-environments/:id',
    name: 'MyDevEnvironmentDetail',
    component: () => import('@/views/MyDevEnvironmentDetail.vue'),
    meta: {
      title: 'Dev Environments - DenvStack',
      description: 'One development environment — workspace, placement and timeline',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    // Phase G v1.1 Sprint 2 — plan-buyer signup wizard. NO requiresAuth so
    // logged-out visitors can complete Step 1 (account) before continuing.
    path: '/buy',
    name: 'BuyPlan',
    component: () => import('@/views/BuyPlan.vue'),
    meta: {
      title: 'Buy Plan - DenvStack',
      description: 'Set up your DenvStack workspace'
    }
  },
  {
    // Tenant lifecycle (auth-api 1.6.0 / cluster-api 2.73.0): where every
    // other /my/* route lands while the ACTIVE organisation is locked or
    // scheduled for deletion — the reason, the deletion date, Pay now and the
    // code-export downloads. The guard below sends an unrestricted org away.
    path: '/my/organisation-locked',
    name: 'OrganisationLocked',
    component: () => import('@/views/OrganisationLocked.vue'),
    meta: {
      title: 'Organisation locked - DenvStack',
      description: 'Your organisation is locked — pay the overdue invoice or download your code',
      requiresAuth: true,
      authModalOnDeny: true
    }
  },
  {
    path: '/my/page',
    name: 'MyPage',
    component: () => import('@/views/MyPage.vue'),
    meta: {
      title: 'My Page - DenvStack',
      description: 'Manage your account and development environments',
      requiresAuth: true
    }
  },
  {
    path: '/changeprofile',
    name: 'Profile',
    component: () => import('@/views/Profile.vue'),
    meta: {
      title: 'Profile Settings - DenvStack',
      description: 'Manage your profile and account settings',
      requiresAuth: true
    }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: {
      title: 'Reset Password - DenvStack',
      description: 'Reset your account password'
    }
  },
  {
    path: '/activate',
    name: 'Activate',
    component: Activate,
    alias: ['/user/activation'],
    meta: {
      title: 'Activate Account - DenvStack',
      description: 'Activate your DenvStack account by setting a password'
    }
  },
  // ─── Legal & compliance pages ──────────────────────────────────────
  // Public, lazy-loaded, no requiresAuth. Paddle reviewers and customers
  // need these reachable from the marketing site without an account.
  // All eight pages share the LegalDocument editorial layout (see
  // src/components/legal/LegalDocument.vue) and use placeholder tokens
  // for the effective / last-reviewed dates (operator fills them in via
  // find-and-replace before publishing — see top-of-file comments in
  // each page for the exact tokens).
  {
    path: '/terms',
    name: 'TermsOfService',
    component: () => import('@/views/legal/TermsOfService.vue'),
    alias: ['/terms-of-service', '/legal/terms-of-service'],
    meta: {
      ...seoMeta(en, 'terms')
    }
  },
  {
    path: '/privacy',
    name: 'PrivacyPolicy',
    component: () => import('@/views/legal/PrivacyPolicy.vue'),
    alias: ['/privacy-policy', '/legal/privacy-policy'],
    meta: {
      ...seoMeta(en, 'privacy')
    }
  },
  {
    path: '/refunds',
    name: 'RefundPolicy',
    component: () => import('@/views/legal/RefundPolicy.vue'),
    alias: ['/refund-policy'],
    meta: {
      ...seoMeta(en, 'refunds')
    }
  },
  {
    path: '/aup',
    name: 'AcceptableUsePolicy',
    component: () => import('@/views/legal/AcceptableUsePolicy.vue'),
    alias: ['/acceptable-use'],
    meta: {
      ...seoMeta(en, 'aup')
    }
  },
  {
    path: '/dpa',
    name: 'DataProcessingAddendum',
    component: () => import('@/views/legal/DataProcessingAddendum.vue'),
    alias: ['/data-processing-addendum'],
    meta: {
      ...seoMeta(en, 'dpa')
    }
  },
  {
    path: '/subprocessors',
    name: 'Subprocessors',
    component: () => import('@/views/legal/Subprocessors.vue'),
    meta: {
      ...seoMeta(en, 'subprocessors')
    }
  },
  {
    // /security — what we hold, how it is protected, who sees what (for the
    // engineering-lead reader; door D links here). The legal security
    // overview (disclosure, incident response, compliance posture) keeps its
    // content at /legal/security and is linked from the bottom of this page.
    path: '/security',
    name: 'Security',
    component: () => import('@/views/Security.vue'),
    meta: {
      ...seoMeta(en, 'security')
    }
  },
  {
    path: '/legal/security',
    name: 'SecurityOverview',
    component: () => import('@/views/legal/Security.vue'),
    alias: ['/security-overview'],
    meta: {
      ...seoMeta(en, 'securityOverview')
    }
  },
  {
    path: '/404',
    name: 'NotFound',
    component: NotFound,
    meta: {
      title: 'Page Not Found - DenvStack'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    } else {
      return { top: 0, behavior: 'smooth' }
    }
  }
})

// Global navigation guards
router.beforeEach((to, from, next) => {
  // Title, description, Open Graph / Twitter tags and the canonical link for
  // every navigation (src/utils/page-meta.js; copy under seo.* in english.json).
  try {
    const siteUrl = getEnvConfig().VUE_APP_PUBLIC_SITE_URL
    applyPageMeta(document, resolvePageMeta(to, en, siteUrl))
  } catch (err) {
    if (to.meta.title) document.title = to.meta.title
    console.warn('page-meta: could not update head tags', err)
  }

  // Security: Check authentication for protected routes
  if (to.meta.requiresAuth && !store.state.auth.isAuthenticated) {
    // Log unauthorized access attempt
    console.warn('Unauthorized access attempt to:', to.path)
    store.dispatch('logSecurityEvent', {
      type: 'unauthorized_access_attempt',
      route: to.path,
      timestamp: new Date().toISOString()
    })

    // Marketplace Phase 2 (design spec §1.2) — for opt-in app routes, open the
    // auth modal in place rather than letting a protected page render blank.
    // We stash the intended path so the login-success path continues here.
    if (to.meta.authModalOnDeny) {
      store.commit('setAuthModalType', 'login')
      store.commit('setShowAuthModal', true)
      store.commit('setPostAuthRedirect', to.fullPath)
      return next(false)
    }
  }

  // Tenant lifecycle: while the ACTIVE organisation is locked or scheduled
  // for deletion, every /my/* route (and the customer dashboard) except
  // Billing, Profile and the locked page itself lands on the locked page.
  // Only the active org decides — switching to another org lifts it.
  if (store.state.auth.isAuthenticated) {
    const lockTarget = lockedRedirectFor(to.path, store.getters['auth/currentUser'])
    if (lockTarget && lockTarget !== to.path) {
      return next({ path: lockTarget, replace: true })
    }
  }

  // Billing permission gate (per current org). When the active org block from
  // /me definitively reports can_view_billing === false, a technical org-admin
  // must not reach the billing surface even via a direct URL — bounce to
  // /my/apps. We only block on an explicit `false`: if the org block hasn't
  // resolved yet (fresh load before fetchMe), we let the view render — it
  // shows its own access-denied state and the nav/guard self-correct once /me
  // arrives. Gating off `current_organization` keeps this reactive to org
  // switches (each switch re-fetches /me).
  if (
    to.meta.requiresBilling &&
    store.state.auth.isAuthenticated
  ) {
    const org = store.getters['auth/currentUser']?.current_organization
    if (org && org.can_view_billing === false) {
      return next({ path: '/my/apps' })
    }
  }

  next()
})

router.afterEach((to) => {
  // Analytics tracking
  if (import.meta.env.PROD && window.gtag) {
    window.gtag('config', 'GA_MEASUREMENT_ID', {
      page_path: to.path
    })
  }

  // Log page view
  store.dispatch('logPageView', {
    path: to.path,
    name: to.name,
    timestamp: new Date().toISOString()
  })
})

// Error handling
router.onError((error, to) => {
  console.error('Router error:', error)

  // Stale-bundle self-heal: after a deploy the hashed chunks this tab's SPA
  // shell points at no longer exist, so lazy routes fail to import (and their
  // CSS fails to preload). Hard-reload onto the target route once per window
  // so the tab picks up the new shell + manifest. Loop-guarded in
  // src/utils/chunk-reload.js via sessionStorage.
  if (reloadForChunkError(error, to && to.fullPath)) return

  // Send error to monitoring service
  store.dispatch('logError', {
    type: 'router_error',
    error: error.message,
    stack: error.stack,
    timestamp: new Date().toISOString()
  })
})

export default router
